<!--
  Public Avatar detail: the shared Preview workspace without effects editing.
  Keeps identity, copy ID, device / layout switches, avatar size & position,
  background preview & download, voice selection and the live conversation;
  `Use in an app` hands the public avatar ID to Integrate.
-->
<script setup lang="ts">
import type { V2PublicAvatar } from '../../../client/types.gen'

import { ArrowLeft } from 'lucide-vue-next'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { publicAvatarServiceListPublicAvatars } from '../../../client'
import CustomAvatarExperience from '../../../components/custom-avatar/CustomAvatarExperience.vue'
import { StButton, StCard } from '../../../components/studio'
import { useApi } from '../../../composables/useApi'
import { logger } from '../../../utils/logger'
import { errorToMessage, toast } from '../../../utils/toast'

import '../../../styles/pages/avatars.css'

// Named so the layout's KeepAlive can exclude this page by name
// (volatile detail data must not be served from cache).
defineOptions({ name: 'PublicAvatarDetailPage' })
definePage({ meta: { desktopAvatarFallback: '/avatars/library' } })

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const avatarId = computed(() => String(route.params.id ?? ''))
const publicAvatar = ref<V2PublicAvatar | null>(null)
const loading = ref(true)
let loadRevision = 0

async function findPublicAvatar(id: string): Promise<V2PublicAvatar | null> {
  let pageToken: string | undefined
  const visitedTokens = new Set<string>()

  do {
    const { data } = (await useApi(publicAvatarServiceListPublicAvatars, {
      query: {
        'pagination.pageSize': 100,
        ...(pageToken ? { 'pagination.pageToken': pageToken } : {}),
      },
    })).expect('Failed to load public avatar')

    const match = data?.publicAvatars?.find(avatar => avatar.id === id)
    if (match)
      return match

    const nextPageToken = data?.pagination?.nextPageToken
    if (!nextPageToken || visitedTokens.has(nextPageToken))
      return null

    visitedTokens.add(nextPageToken)
    pageToken = nextPageToken
  } while (pageToken)

  return null
}

watch(avatarId, async (id) => {
  const revision = ++loadRevision
  loading.value = true
  publicAvatar.value = null
  try {
    const found = id ? await findPublicAvatar(id) : null
    if (revision !== loadRevision)
      return
    publicAvatar.value = found
  }
  catch (cause) {
    if (revision !== loadRevision)
      return
    // The request failure is reported by useApi (api_request_failed).
    logger.warn('Failed to load public avatar:', cause)
    toast.error(t('avatars.edit.loadFailed'), { description: errorToMessage(cause) })
  }
  finally {
    if (revision === loadRevision)
      loading.value = false
  }
}, { immediate: true })

onUnmounted(() => {
  loadRevision++
})

function close() {
  router.push('/avatars/library')
}

// Integrate hand-off carries the public avatar ID.
function useInApp() {
  router.push({ path: '/integrate', query: { avatarId: avatarId.value, avatarSource: 'public' } })
}
</script>

<template>
  <div class="studio-ui avatars-page">
    <CustomAvatarExperience
      v-if="publicAvatar"
      :key="avatarId"
      :avatar-id="avatarId"
      :name="publicAvatar.name"
      :cover-url="publicAvatar.coverUrl"
      :background-image-url="publicAvatar.backgroundImageUrl"
      initial-mode="tune"
      :effects-editable="false"
      :back-label="t('page.library.detail.back')"
      @close="close"
    >
      <template #actions>
        <StButton variant="primary" data-testid="public-avatar-use-in-app" @click="useInApp">
          {{ t('page.library.detail.useInApp') }}
        </StButton>
      </template>
    </CustomAvatarExperience>

    <div v-else-if="loading" class="workspace-loading" role="status" :aria-label="t('common.loading')">
      <span class="st-spinner" aria-hidden="true" />
    </div>

    <StCard v-else class="workspace-unavailable">
      <h2>{{ t('page.library.detail.unavailable') }}</h2>
      <StButton @click="close">
        <ArrowLeft :size="16" :stroke-width="1.65" aria-hidden="true" />{{ t('page.library.detail.back') }}
      </StButton>
    </StCard>
  </div>
</template>
